/**
 * LL2の英語ミッション名 → 日本語の公式名称。
 *
 * なぜ要るか: LL2は `Michibiki 7 (QZS-7)` `Lunar Polar Exploration Mission (LUPEX)` のように
 * 英語しか返さない。日本語の検索者が打つのは「みちびき7号機」「月極域探査機」なので、
 * 英語のまま出すと日本語のミッション名で探している人に一切届かない。
 *
 * ★規律（ここが緩むと嘘の台帳になる）
 *  - **一次情報（JAXA・内閣府・QZSS公式・三菱重工・ISAS・IST・ispace等）で確認できたものだけ**。
 *    ニュース・Wikipedia・まとめ記事は出典として認めない。
 *  - `sourceUrl` は必須。実際に開いて当該表記があることを確認したURLのみ。
 *  - 表記は公式のまま（「号」「号機」を勝手に整えない）。
 *  - 確認できなかった回は**ここに載せない**＝英語名のまま表示される。それが正しい挙動。
 *
 * ★2026-07-31 の調査で意図的に見送ったもの
 *  - `MBR Explorer`: 三菱重工の日本語リリースでも `探査機「MBR Explorer」` と英語のままで、
 *    **日本語の固有名が存在しない**。カナを当てるのは捏造なので載せない。
 *  - `DESTINY+ & RAMSES` の RAMSES 側: ESA機で和名なし。両者を連結した公式表記も存在しない。
 *    → 和名は DESTINY+ 側だけにし、英語名の併記で RAMSES が落ちないようにしている。
 *
 * ★表記の正規化を1点だけ行っている（事実の変更ではない）
 *  - `DESTINY⁺`（U+207A 上付きプラス）→ `DESTINY+`（ASCIIプラス）。
 *    上付きプラスはフォントによっては欠字（□）になるため。読みは変わらない。
 *  - `HTV-X３号機`（内閣府PDFは全角３）→ `HTV-X3号機`（半角）。
 *    同じ台帳内の `HTV-X2号機`（JAXA表記・半角）と数字幅がそろわないため。数字の幅のみの調整。
 */

export interface MissionNameJa {
  /** LL2 の mission フィールド（完全一致で引く） */
  ll2Mission: string;
  /**
   * 同じ mission 名が別の機体でも起こりうるとき、機体名で絞る。
   * 例: `Maiden Flight` は「初飛行」という一般名詞なので、
   *     ZERO以外の機体の初飛行に「ZERO初号機」を当ててしまわないよう縛る。
   */
  ll2Rocket?: string;
  /** 日本語の公式名称 */
  ja: string;
  /** 実際に開いて表記を確認した一次情報URL */
  sourceUrl: string;
}

export const MISSION_NAMES_JA: MissionNameJa[] = [
  {
    ll2Mission: 'Michibiki 7 (QZS-7)',
    ja: 'みちびき7号機',
    sourceUrl: 'https://qzss.go.jp/7satellite-constellation/index.html',
  },
  {
    ll2Mission: 'Martian Moon eXplorer (MMX)',
    ja: '火星衛星探査計画（MMX）',
    sourceUrl: 'https://www.jaxa.jp/projects/sas/mmx/index_j.html',
  },
  {
    ll2Mission: 'ETS-9',
    ja: '技術試験衛星9号機（ETS-9）',
    sourceUrl: 'https://www.satnavi.jaxa.jp/ja/project/ets-9/',
  },
  {
    ll2Mission: 'HTV-X2',
    ja: 'HTV-X2号機',
    sourceUrl: 'https://humans-in-space.jaxa.jp/htv-x/news/cargo/230714.html',
  },
  {
    // 内閣府「宇宙基本計画工程表」に全角で `HTV-X３号機`。数字幅のみ半角に正規化している。
    ll2Mission: 'HTV-X3',
    ja: 'HTV-X3号機',
    sourceUrl: 'https://www8.cao.go.jp/space/plan/plan2/kaitei_fy07/kaitei_fy0712.pdf',
  },
  {
    ll2Mission: 'Lunar Polar Exploration Mission (LUPEX)',
    ja: '月極域探査機（LUPEX）',
    sourceUrl: 'https://humans-in-space.jaxa.jp/biz-lab/news/detail/004937.html',
  },
  {
    ll2Mission: 'JV-LOTUSat-1',
    ja: 'ベトナム向け地球観測衛星「LOTUSat-1」',
    sourceUrl: 'https://www.jaxa.jp/press/2020/06/20200612-1-1_j.html',
  },
  {
    ll2Mission: 'SOLAR-C',
    ja: '高感度太陽紫外線分光観測衛星（SOLAR-C）',
    sourceUrl: 'https://www.jaxa.jp/projects/sas/solar-c/index_j.html',
  },
  {
    // 「Maiden Flight」は一般名詞なので機体で縛る（他機の初飛行に当てない）
    ll2Mission: 'Maiden Flight',
    ll2Rocket: 'ZERO',
    ja: 'ZERO初号機',
    sourceUrl: 'https://www.istellartech.com/news/releases/10228',
  },
  {
    // 公式表記は `DESTINY⁺`（U+207A）。フォント欠字を避けてASCIIプラスに正規化。
    // RAMSES（ESA機）に和名は無いため、和名はDESTINY+側のみ。英語名は併記で残る。
    ll2Mission: 'DESTINY+ & RAMSES',
    ja: '深宇宙探査技術実証機 DESTINY+',
    sourceUrl: 'https://www.isas.jaxa.jp/missions/spacecraft/developing/destiny_plus.html',
  },
  {
    ll2Mission: 'ispace Mission 3 ULTRA',
    ja: 'ミッション3「ULTRA」',
    sourceUrl: 'https://www.mhi.com/jp/news/260729.html',
  },
];

/** 英語ミッション名に対応する和名。未確認なら null（呼び出し側は英語名のまま出す）。 */
export function missionJa(missionEn: string | null | undefined, rocket?: string | null): string | null {
  if (!missionEn) return null;
  const key = missionEn.trim();
  const hit = MISSION_NAMES_JA.find(
    (m) => m.ll2Mission === key && (!m.ll2Rocket || (rocket ?? '').trim().startsWith(m.ll2Rocket)),
  );
  return hit?.ja ?? null;
}

/**
 * 台帳の健全性チェック（ビルド時に呼ぶ）。
 * 出典の無い和名を混入させないための最後の砦。
 */
export function assertMissionNamesValid(): void {
  const seen = new Set<string>();
  for (const m of MISSION_NAMES_JA) {
    if (!m.ll2Mission || !m.ja) throw new Error(`mission-names-ja: 名称が空です: ${JSON.stringify(m)}`);
    if (!/^https:\/\//.test(m.sourceUrl || '')) {
      throw new Error(`mission-names-ja: 「${m.ll2Mission}」に一次情報URLがありません（和名は出典必須）`);
    }
    const k = `${m.ll2Mission} ${m.ll2Rocket ?? ''}`;
    if (seen.has(k)) throw new Error(`mission-names-ja: 重複エントリ: ${m.ll2Mission}`);
    seen.add(k);
  }
}
